/* ====================================================================
   looxsmax 7-in-1 LED Facial Sculpting Device — product page JS
   - Reads product data from window.fspProductData (set by hero section)
   - Variant picker drives the Shopify product form
   - Sticky add-to-cart bar mirrors variant state
   - Gallery, qty, countdown, reveal animations
   Vanilla JS, no dependencies.
   ==================================================================== */
(function () {
  'use strict';

  /* ---------- Announce rotator (mobile) ---------- */
  const announceItems = document.querySelectorAll('.fsp-announce-item');
  if (announceItems.length > 1) {
    announceItems[0].classList.add('is-active');
    let aIdx = 0;
    setInterval(() => {
      announceItems[aIdx].classList.remove('is-active');
      aIdx = (aIdx + 1) % announceItems.length;
      announceItems[aIdx].classList.add('is-active');
    }, 3500);
  }

  /* ---------- Reveal animations ---------- */
  if ('IntersectionObserver' in window) {
    const revealEls = document.querySelectorAll('[data-fsp-reveal]');
    if (revealEls.length) {
      const io = new IntersectionObserver((entries) => {
        entries.forEach(e => {
          if (e.isIntersecting) {
            e.target.classList.add('is-in');
            io.unobserve(e.target);
          }
        });
      }, { threshold: 0.08 });
      revealEls.forEach(el => io.observe(el));
    }
  }

  /* ---------- Gallery (hero) ---------- */
  document.querySelectorAll('[data-fsp-gallery]').forEach(initGallery);
  function initGallery(root) {
    const slides = root.querySelectorAll('.fsp-gallery-slide');
    const dots   = root.querySelectorAll('.fsp-gallery-nav button');
    const thumbs = root.querySelectorAll('.fsp-thumb');
    if (!slides.length) return;

    function setSlide(i) {
      slides.forEach((s, idx) => s.classList.toggle('is-active', idx === i));
      dots.forEach((d, idx) => d.classList.toggle('is-active', idx === i));
      thumbs.forEach((t, idx) => t.classList.toggle('is-active', idx === i));
    }
    dots.forEach((d, i)   => d.addEventListener('click', () => setSlide(i)));
    thumbs.forEach((t, i) => t.addEventListener('click', () => setSlide(i)));

    if (root.dataset.fspAutoadvance === 'true' && slides.length > 1) {
      let gIdx = 0;
      setInterval(() => {
        gIdx = (gIdx + 1) % slides.length;
        setSlide(gIdx);
      }, 5000);
    }

    // Variant -> jump to associated media when variant changes
    root.addEventListener('fsp:variantChange', (e) => {
      const variant = e.detail && e.detail.variant;
      if (!variant || !variant.featured_media) return;
      const idx = Array.from(slides).findIndex(s => String(s.dataset.fspMediaId) === String(variant.featured_media.id));
      if (idx >= 0) setSlide(idx);
    });
  }

  /* ---------- Quantity ---------- */
  document.querySelectorAll('[data-fsp-qty]').forEach(initQty);
  function initQty(root) {
    const input = root.querySelector('input');
    const minus = root.querySelector('.fsp-qty-minus');
    const plus  = root.querySelector('.fsp-qty-plus');
    if (!input) return;
    function clamp(v) {
      const min = parseInt(input.getAttribute('min'), 10) || 1;
      const max = parseInt(input.getAttribute('max'), 10) || 99;
      return Math.max(min, Math.min(max, parseInt(v, 10) || min));
    }
    minus && minus.addEventListener('click', () => { input.value = clamp(parseInt(input.value, 10) - 1); });
    plus  && plus.addEventListener('click',  () => { input.value = clamp(parseInt(input.value, 10) + 1); });
    input.addEventListener('change', () => { input.value = clamp(input.value); });
  }

  /* ---------- Product variant picker ---------- */
  const productRoot = document.querySelector('[data-fsp-product]');
  if (productRoot && window.fspProductData) {
    const productData = window.fspProductData;
    const form = productRoot.querySelector('form[action*="/cart/add"]');
    const variantIdInput = form ? form.querySelector('[name="id"]') : null;
    const gallery = productRoot.querySelector('[data-fsp-gallery]');

    const optionInputs = Array.from(productRoot.querySelectorAll('.fsp-variant-option input[type=radio]'));

    function currentSelections() {
      const sel = {};
      optionInputs.forEach(inp => {
        if (inp.checked) sel[inp.dataset.optionIndex] = inp.value;
      });
      return sel;
    }

    function findMatchingVariant() {
      const sel = currentSelections();
      return productData.variants.find(v => {
        return v.options.every((opt, idx) => String(sel[idx]) === String(opt));
      });
    }

    function updateForVariant(variant) {
      if (!variant) return;
      if (variantIdInput) variantIdInput.value = variant.id;

      // Price
      const priceEl = productRoot.querySelector('[data-fsp-price]');
      const compareEl = productRoot.querySelector('[data-fsp-compare-price]');
      if (priceEl) priceEl.textContent = formatMoney(variant.price);
      if (compareEl) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          compareEl.textContent = formatMoney(variant.compare_at_price);
          compareEl.style.display = '';
        } else {
          compareEl.style.display = 'none';
        }
      }

      // CTA label & state
      const cta = productRoot.querySelector('[data-fsp-cta]');
      const ctaLabel = productRoot.querySelector('[data-fsp-cta-label]');
      const soldoutBanner = productRoot.querySelector('[data-fsp-soldout]');
      if (cta) {
        if (!variant.available) {
          cta.setAttribute('disabled', 'disabled');
          if (ctaLabel) ctaLabel.textContent = window.fspStrings && window.fspStrings.soldOut ? window.fspStrings.soldOut : 'Sold out';
          soldoutBanner && (soldoutBanner.style.display = '');
        } else {
          cta.removeAttribute('disabled');
          const ctaText = productRoot.dataset.fspCtaText || 'Add to Cart';
          if (ctaLabel) ctaLabel.textContent = ctaText + ' — ' + formatMoney(variant.price);
          soldoutBanner && (soldoutBanner.style.display = 'none');
        }
      }

      // Sticky cart
      const stickyPrice = document.querySelector('[data-fsp-sticky-price]');
      const stickyCompare = document.querySelector('[data-fsp-sticky-compare]');
      const stickyVariantId = document.querySelector('[data-fsp-sticky] [name="id"]');
      const stickyCta = document.querySelector('[data-fsp-sticky] [data-fsp-cta]');
      if (stickyPrice) stickyPrice.textContent = formatMoney(variant.price);
      if (stickyCompare) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          stickyCompare.textContent = formatMoney(variant.compare_at_price);
          stickyCompare.style.display = '';
        } else { stickyCompare.style.display = 'none'; }
      }
      if (stickyVariantId) stickyVariantId.value = variant.id;
      if (stickyCta) {
        if (!variant.available) stickyCta.setAttribute('disabled', 'disabled');
        else stickyCta.removeAttribute('disabled');
      }

      // Notify gallery
      if (gallery) {
        gallery.dispatchEvent(new CustomEvent('fsp:variantChange', { detail: { variant } }));
      }

      // Update URL with variant
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      } catch (e) { /* ignore */ }
    }

    function handleChange() {
      const variant = findMatchingVariant();
      updateForVariant(variant);
    }

    optionInputs.forEach(inp => inp.addEventListener('change', handleChange));

    // Run once on load
    const initial = findMatchingVariant();
    if (initial) updateForVariant(initial);
  }

  /* ---------- Sticky add-to-cart visibility ---------- */
  const stickyCart = document.querySelector('[data-fsp-sticky]');
  const heroEl = document.querySelector('[data-fsp-product]');
  if (stickyCart && heroEl && 'IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        stickyCart.classList.toggle('is-visible', !e.isIntersecting);
      });
    }, { threshold: 0, rootMargin: '-200px 0px 0px 0px' });
    io.observe(heroEl);
  }

  /* ---------- Scarcity countdown ---------- */
  document.querySelectorAll('[data-fsp-countdown]').forEach(initCountdown);
  function initCountdown(el) {
    const h = parseInt(el.dataset.fspHours, 10) || 6;
    const m = parseInt(el.dataset.fspMinutes, 10) || 0;
    const s = parseInt(el.dataset.fspSeconds, 10) || 0;
    let total = h * 3600 + m * 60 + s;
    const seed = total;
    function pad(n) { return String(n).padStart(2, '0'); }
    function tick() {
      if (total <= 0) total = seed;
      const hh = Math.floor(total / 3600);
      const mm = Math.floor((total % 3600) / 60);
      const ss = total % 60;
      el.textContent = pad(hh) + 'h : ' + pad(mm) + 'm : ' + pad(ss) + 's';
      total--;
    }
    tick();
    setInterval(tick, 1000);
  }

  /* ---------- Money helper ---------- */
  function formatMoney(cents) {
    if (window.Shopify && typeof Shopify.formatMoney === 'function' && window.fspMoneyFormat) {
      return Shopify.formatMoney(cents, window.fspMoneyFormat);
    }
    const sym = (window.fspMoneyFormat && window.fspMoneyFormat.match(/^[^{]+/) || ['$'])[0].trim() || '$';
    return sym + (cents / 100).toFixed(2);
  }

})();
